// frontend/user/src/components/file-actions.tsx
//
// 2026-05-28 重写(BQOCz BottomSheet 对齐)。
// ui/dogdisk-user.pen Node ID: BQOCz(ActionSheet)。
//
// 主 sheet:FileInfo header + 6 action row(下载/分享/重命名/移动到/复制到/详细信息)
// + divider + 删除 row(红色)。
// 重命名走 FoNDD 底部弹层(modals/rename-dialog.tsx,2026-06-07 对齐设计稿)。
//
// 2026-05-28 下载两段式重构合并:onDownload 改 useMutation
// (GET /gapi/user/download/{id} 返 {url} JSON → window.location.href = url 触发浏览器原生下载;
// 新 stream endpoint /gapi/file/{base_file_id}/{filename} 公开无鉴权;
// 详见 docs/superpowers/specs/2026-05-28-download-twostage-design.md)。
//
// V1 范围:
//   下载 ✓ GET /gapi/user/download/:id → resp.url redirect
//   分享 ✓ navigate /pack/create?file_ids=:id(多选批量入口统一)
//   重命名 ✓ PATCH /gapi/user/files/:id
//   移动到 ✓ GQRct(move-batch) MoveFileDialog 接通(2026-06-07)
//   复制到 ✓ KVHpM(/copy) MoveFileDialog 接通(2026-06-07)
//   详细信息 ✓ navigate fileOpenHref(按类型分流;2026-05-31 对齐 lFtCI)
//   删除 ✓ POST /gapi/user/files/:id/trash
//
// 文件夹隐藏 "下载"(BQOCz context 明示)。

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, formatBytes } from '@dogdisk/shared'
import { DeleteConfirmDialog } from './modals/delete-confirm-dialog'
import { RenameDialog } from './modals/rename-dialog'
import { MoveFileDialog } from './move-file-dialog'
import { fileOpenHref } from '../_lib/file-route'
import { resourceUrl } from '../_lib/resource-node-pref'

type FileLike = {
  id: string
  name: string
  type: string
  mime_type?: string | null
  size?: number | null
  parent_id: string | null
}

export type FileActionsProps = {
  file: FileLike
  onClose: () => void
  parentId: string | null
}

// 文件类型 → BQOCz fileIcon 颜色(对齐 BQOCz 设计稿 fileIcon #FF6B6B,语义为非文件夹通用)。
// 文件夹用其他色块以示区分(BQOCz 设计稿示例是 PDF 用红色;此处按文件 type 简化)。
function fileIconColor(type: string): string {
  if (type === 'folder') return '#60A5FA'
  if (type === 'image') return '#9333EA'
  if (type === 'video') return '#F57C00'
  if (type === 'pdf') return '#FF6B6B'
  return '#9CA3AF'
}

export function FileActions({ file, onClose, parentId }: FileActionsProps) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [renameOpen, setRenameOpen] = useState(false)
  const [moveMode, setMoveMode] = useState<'move' | 'copy' | null>(null)
  const [error, setError] = useState<string | null>(null)
  // 2026-05-29 接入 mdYih 删除确认 modal — 替换直接 trashMut.mutate() 的无确认流
  const [confirmDelete, setConfirmDelete] = useState(false)

  // ESC 关闭 BQOCz BottomSheet(spec 期望键盘可关闭)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !renameOpen && !confirmDelete && !moveMode) onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [renameOpen, confirmDelete, moveMode, onClose])

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['file', 'list', parentId] })
  }

  const renameMut = useMutation({
    mutationFn: (newName: string) =>
      apiClient.patch(`/gapi/user/files/${file.id}`, { new_name: newName }),
    onSuccess: () => {
      invalidate()
      setRenameOpen(false)
      onClose()
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'rename failed'),
  })

  const trashMut = useMutation({
    mutationFn: () => apiClient.post(`/gapi/user/files/${file.id}/trash`, {}),
    onSuccess: () => {
      invalidate()
      onClose()
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'trash failed'),
  })

  const downloadMut = useMutation({
    mutationFn: () => apiClient.get<{ url: string; expires_at: string }>(`/gapi/user/download/${file.id}`),
    onSuccess: (resp) => {
      if (!resp.url) {
        setError('下载链接为空')
        return
      }
      window.location.href = resourceUrl(resp.url)
      onClose()
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'download failed'),
  })

  const onDownload = () => {
    setError(null)
    downloadMut.mutate()
  }

  const onShare = () => {
    onClose()
    navigate(`/pack/create?file_ids=${file.id}`)
  }

  const onMove = () => setMoveMode('move')

  const onCopy = () => setMoveMode('copy')

  // 2026-05-31 对齐 ui/dogdisk-user.pen lFtCI(action6 详细信息):走统一裁决点 fileOpenHref。
  //   文件夹→/folders/:id(tC8FY);其余一律 →/file/:id(FilePreviewPage 按 mime 渲染
  //   图片 zkY66 / 视频 kENKt / 音频 qCOtS / 其他 pKcdM 兜底)。
  // 与 file-browser 行点击同一函数,保证「详细信息」与行点击落同一页。
  const onDetail = () => {
    onClose()
    navigate(fileOpenHref(file))
  }

  const isFolder = file.type === 'folder'

  return (
    <>
      {/* BQOCz BottomSheet 主 sheet — 重命名展开或移动/复制时隐藏 */}
      {!renameOpen && !moveMode && (
        <div
          data-frame-id="BQOCz"
          data-testid="BQOCz"
          role="dialog"
          aria-modal="true"
          aria-label="文件操作"
          className="fixed inset-0 z-40 flex items-end bg-black/50"
          onClick={onClose}
        >
          <div
            className="mx-auto w-full max-w-[402px] rounded-t-3xl bg-white"
            style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* HandleBar */}
            <div className="flex h-6 items-center justify-center">
              <span className="h-1 w-10 rounded-full bg-[#E5E7EB]" />
            </div>

            {/* FileInfo:icon 44 + 文件名 19/600 + size 17 */}
            <div className="flex items-center gap-[14px] px-6 py-4">
              <span
                className="flex h-11 w-11 items-center justify-center rounded-lg"
                style={{ backgroundColor: `${fileIconColor(file.type)}1A`, color: fileIconColor(file.type) }}
              >
                {isFolder ? (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8.5a2 2 0 0 1-1.7-.9l-.81-1.22A2 2 0 0 0 7.93 3H4Z" />
                  </svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2Z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[19px] font-semibold text-[#1A1A1A]">{file.name}</p>
                <p className="text-[14px] text-[#9CA3AF]">
                  {isFolder ? '文件夹' : file.size ? formatBytes(file.size) : ''}
                </p>
              </div>
            </div>

            {/* divider */}
            <div className="h-2 bg-[#F6F7F8]" />

            {/* ActionList */}
            <ul className="flex flex-col">
              {!isFolder && (
                <li>
                  <button
                    type="button"
                    data-testid="BQOCz-action-download"
                    onClick={onDownload}
                    disabled={downloadMut.isPending}
                    className="flex h-14 w-full items-center gap-4 px-6 text-left active:bg-[#F6F7F8] disabled:opacity-50"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" x2="12" y1="15" y2="3" />
                    </svg>
                    <span className="text-[19px] text-[#1A1A1A]">{downloadMut.isPending ? '处理中…' : '下载'}</span>
                  </button>
                </li>
              )}
              {/* 分享 = 打成资源包(/pack/create);文件夹不可分享/打包(spec D3) */}
              {!isFolder && (
                <li>
                  <button
                    type="button"
                    data-testid="BQOCz-action-share"
                    onClick={onShare}
                    className="flex h-14 w-full items-center gap-4 px-6 text-left active:bg-[#F6F7F8]"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" />
                      <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />
                    </svg>
                    <span className="text-[19px] text-[#1A1A1A]">分享</span>
                  </button>
                </li>
              )}
              <li>
                <button
                  type="button"
                  data-testid="BQOCz-action-rename"
                  onClick={() => {
                    setError(null)
                    setRenameOpen(true)
                  }}
                  className="flex h-14 w-full items-center gap-4 px-6 text-left active:bg-[#F6F7F8]"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                  </svg>
                  <span className="text-[19px] text-[#1A1A1A]">重命名</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  data-testid="BQOCz-action-move"
                  onClick={onMove}
                  className="flex h-14 w-full items-center gap-4 px-6 text-left active:bg-[#F6F7F8]"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M2 9a2 2 0 0 1 2-2h3.93a2 2 0 0 1 1.66.9l.82 1.2a2 2 0 0 0 1.66.9H20a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9Z" />
                    <path d="M2 9.5V4a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2" />
                  </svg>
                  <span className="text-[19px] text-[#1A1A1A]">移动到</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  data-testid="BQOCz-action-copy"
                  onClick={onCopy}
                  className="flex h-14 w-full items-center gap-4 px-6 text-left active:bg-[#F6F7F8]"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="8" y="8" width="14" height="14" rx="2" ry="2" />
                    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                  </svg>
                  <span className="text-[19px] text-[#1A1A1A]">复制到</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  data-testid="BQOCz-action-detail"
                  onClick={onDetail}
                  className="flex h-14 w-full items-center gap-4 px-6 text-left active:bg-[#F6F7F8]"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" x2="12" y1="16" y2="12" />
                    <line x1="12" x2="12.01" y1="8" y2="8" />
                  </svg>
                  <span className="text-[19px] text-[#1A1A1A]">详细信息</span>
                </button>
              </li>
            </ul>

            {/* divider2 */}
            <div className="h-2 bg-[#F6F7F8]" />

            {/* deleteAction(红色) — 弹 mdYih 二次确认(2026-05-29) */}
            <button
              type="button"
              data-testid="BQOCz-action-delete"
              disabled={trashMut.isPending}
              onClick={() => setConfirmDelete(true)}
              className="flex h-14 w-full items-center gap-4 px-6 text-left active:bg-[#FFF5F5] disabled:opacity-50"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 6h18" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" x2="10" y1="11" y2="17" />
                <line x1="14" x2="14" y1="11" y2="17" />
              </svg>
              <span className="text-[19px] text-[#FF6B6B]">{trashMut.isPending ? '删除中…' : '移到回收站'}</span>
            </button>

            {error && <p className="px-6 pb-2 text-[12px] text-[#FF6B6B]">{error}</p>}
          </div>
        </div>
      )}

      {/* FoNDD 重命名底部弹层(2026-06-07 对齐设计稿) */}
      <RenameDialog
        open={renameOpen}
        initialName={file.name}
        busy={renameMut.isPending}
        error={error}
        onSubmit={(n) => {
          setError(null)
          renameMut.mutate(n)
        }}
        onClose={() => setRenameOpen(false)}
      />

      {/* GQRct/KVHpM 移动/复制目标选择(2026-06-07 接通) */}
      {moveMode && (
        <MoveFileDialog
          files={[file]}
          mode={moveMode}
          onClose={() => setMoveMode(null)}
          onDone={() => {
            setMoveMode(null)
            onClose()
          }}
        />
      )}

      {/* mdYih 删除确认 — BQOCz deleteAction 触发(2026-05-29) */}
      <DeleteConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false)
          trashMut.mutate()
        }}
        title={isFolder ? '删除文件夹?' : '删除文件?'}
        subtitle={
          isFolder
            ? '文件夹及其内容将移入回收站,30 天后自动清除。'
            : '文件将移入回收站,30 天后自动清除。'
        }
        confirmText="删除"
        loading={trashMut.isPending}
      />
    </>
  )
}
